import {
  appFormUtil,
  gqlCacheUtil
} from 'modules/common';
import React, { useEffect } from 'react';
import { DateTime } from 'luxon';
import {
  useSaveInspectionMutation,
  SaveInspection_Input,
  useInspectionFormQuery,
  DailyReportDetailSectionQuery,
  SaveInspectionMutation,
  DailyReportDetailSectionDocument
} from 'generated/graphql';
import _ from 'lodash';

export type IFormState = {
  dateInspected: string;
  description: string;
  id: string;
  inspectedById: string;
  newInspectorFirstName: string;
  newInspectorLastName: string;
  inspectedSystem: string;
  inspectionResultId: string;
  milestoneId: string;
  projectId: string;
};

const initialState: IFormState = {
  dateInspected: DateTime.now().toFormat('yyyy-LL-dd'),
  description: '',
  id: '',
  inspectedById: '',
  newInspectorFirstName: '',
  newInspectorLastName: '',
  inspectedSystem: '',
  inspectionResultId: '',
  milestoneId: '',
  projectId: ''
};

export function useFormState(id: string, projectId: string) {
  const { formState, dispatch, genFieldProps } = appFormUtil.useFormState({
    initialState,
    key2FieldConfig: {
      dateInspected: {
        label: 'Inspection Date',
        type: 'date'
      },
      description: {
        label: 'Description',
        type: 'text'
      },
      id: {
        label: 'Id',
        type: 'text'
      },
      inspectedById: {
        label: 'Inspected by',
        type: 'text'
      },
      newInspectorFirstName: {
        label: 'Inspector FirstName',
        type: 'text'
      },
      newInspectorLastName: {
        label: 'Inspector Last Name',
        type: 'text'
      },
      inspectedSystem: {
        label: 'Inspected system',
        type: 'text'
      },
      inspectionResultId: {
        label: 'Inspection result',
        type: 'text'
      },
      milestoneId: {
        label: 'Milestone',
        type: 'text'
      },
      projectId: {
        label: 'Project',
        type: 'text'
      }
    }
  });

  useEffect(() => {
    if (formState.projectId === '' && projectId !== '') {
      dispatch({
        type: 'UPDATE',
        payload: {
          property: 'projectId',
          value: projectId
        }
      });
    }
  }, [
    dispatch,
    formState.projectId,
    projectId
  ]);

  const { data, loading, called } = useQuery(id);

  useEffect(() => {
    if (data.id !== '', formState.id === '' && !loading && called) {
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          ...formState,
          ...data
        }
      });
    }
  });

  return React.useMemo(
    () => {

      return {
        formState,
        dispatch,
        genFieldProps
      };
    },
    [
      dispatch,
      formState,
      genFieldProps
    ]
  );
}

export function useSaveInspection() {
  const [
    fnMutate,
    tuple
  ] = useSaveInspectionMutation({
    refetchQueries: []
  });
  const fnWrappedMutate = React.useCallback(
    async (input: SaveInspection_Input) => {
      await fnMutate({
        variables: {
          input
        },
        updateQueries: {
          ...gqlCacheUtil.genUpdateQueryMap<DailyReportDetailSectionQuery, SaveInspectionMutation>(DailyReportDetailSectionDocument, (prevState, { mutationResult }) => {
            if (!prevState.dailyReport?.id) {
              return prevState;
            }
            if (!mutationResult.data?.saveInspection?.id) {
              return prevState;
            }

            const isNew = input.id !== mutationResult.data.saveInspection.id;
            if (isNew) {
              return {
                ...prevState,
                dailyReport: {
                  ...prevState.dailyReport,
                  inspections: _.compact([
                    ...prevState.dailyReport.inspections,
                    mutationResult.data.saveInspection
                  ])
                }
              };
            }

            return {
              ...prevState,
              dailyReport: {
                ...prevState.dailyReport,
                inspections: _.compact([
                  ...prevState.dailyReport.inspections.map((node) => {
                    if (node.id === mutationResult.data?.saveInspection?.id) {
                      return {
                        ...node,
                        ...mutationResult.data.saveInspection
                      };
                    }
                    return node;
                  })
                ])
              }

            };


          })
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}


export interface IData extends Omit<IFormState, 'milestoneId' | 'newInspectorFirstName' | 'newInspectorLastName'> {}

export function useQuery(id: string) {
  const tuple = useInspectionFormQuery({
    variables: {
      id
    },
    skip: id === ''
  });

  return React.useMemo(
    () => {
      const data: IData = {
        id: tuple.data?.inspection?.id ?? '',
        dateInspected: tuple.data?.inspection?.dateInspected.toFormat('yyyy-LL-dd') ?? '',
        description: tuple.data?.inspection?.description ?? '',
        inspectedById: tuple.data?.inspection?.inspectedBy.id ?? '',
        inspectedSystem: tuple.data?.inspection?.inspectedSystem ?? '',
        inspectionResultId: tuple.data?.inspection?.inspectionResult?.id ?? '',
        projectId: tuple.data?.inspection?.project.id ?? ''
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}
